import type { EntityKind, Entities } from "@/lib/types";
import { ENTITY_KINDS } from "@/lib/constants";

export function relTime(ts: number): string {
  const diff = Math.max(0, Date.now() - ts);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s fa`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h fa`;
  const d = Math.floor(h / 24);
  return `${d} g fa`;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function fmtClock(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** HH:MM (without seconds) — preferred for article meta rows. */
export function fmtClockShort(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fmtDate(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Oltre il mese il conto a mente non si fa più: "47 giorni fa" obbliga a
 *  ricostruire la data invece di darla. */
const UN_MESE_G = 30;

/**
 * Il tempo trascorso, a parole intere: "3 giorni fa", non "3 g fa".
 *
 * `relTime` è la forma compatta del rullo, dove il valore sta in coda a
 * una riga insieme al codice del dispaccio e al conteggio dei
 * riferimenti, e ogni carattere in meno è spazio guadagnato. Nella card
 * di un dossier invece quella riga porta solo un'etichetta e una
 * rubrica: lì l'abbreviazione non fa risparmiare niente e si legge come
 * un errore di stampa.
 *
 * Un giorno esatto diventa "ieri", che è come lo direbbe una persona.
 * Oltre il mese torna la data: di un dossier di quell'età conta quando è
 * uscito, non da quanto tempo è lì.
 */
export function relTimeLong(ts: number): string {
  const diff = Math.max(0, Date.now() - ts);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "adesso";
  if (min < 60) return `${min} ${min === 1 ? "minuto" : "minuti"} fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ${h === 1 ? "ora" : "ore"} fa`;
  const g = Math.floor(h / 24);
  if (g === 1) return "ieri";
  if (g <= UN_MESE_G) return `${g} giorni fa`;
  return fmtDate(ts);
}

/** True if two timestamps fall on the same calendar day in local time. */
export function isSameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

const GIORNI_IT = [
  "domenica",
  "lunedì",
  "martedì",
  "mercoledì",
  "giovedì",
  "venerdì",
  "sabato",
];
const MESI_IT = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
];

/**
 * L'etichetta di una giornata, per gli stacchi del rullo:
 * «giovedì 18 settembre 2026».
 *
 * Scritta a mano e non con `toLocaleDateString("it-IT", …)` per la
 * stessa ragione per cui nel progetto è scritta a mano `fmtDate`: il
 * formato deve uscire **identico** sul server e nel browser, e le due
 * ICU non sono lo stesso oggetto. Una differenza di una virgola o di
 * una maiuscola fra Node e Chrome è un errore di idratazione su ogni
 * stacco della lista.
 *
 * L'anno c'è sempre, e non solo quando è diverso da quello corrente:
 * «quello corrente» vuol dire leggere l'orologio, e un valore che
 * dipende da `Date.now()` dentro il render è la stessa trappola. In
 * più il rullo, sfogliato, entra nell'archivio storico e arriva al
 * 2009: là l'anno non è un di più, è il dato.
 */
export function etichettaGiorno(ts: number): string {
  const d = new Date(ts);
  return `${GIORNI_IT[d.getDay()]} ${d.getDate()} ${MESI_IT[d.getMonth()]} ${d.getFullYear()}`;
}

/** L'etichetta di un mese: «settembre 2026». */
export function etichettaMese(ts: number): string {
  const d = new Date(ts);
  return `${MESI_IT[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * La data di un pezzo firmato: «13 settembre 2026».
 *
 * Senza il giorno della settimana, che è il di più di `etichettaGiorno`
 * (quella apre lo stacco di una giornata in una lista fitta, e là
 * «giovedì» serve a collocare), e **senza l'ora**: di un editoriale
 * conta il giorno in cui è uscito, non il minuto. È la forma che si
 * mette accanto a una firma e l'unica che si può citare.
 *
 * Scritta a mano come tutte le date italiane di questo file, e per la
 * stessa ragione: `toLocaleDateString("it-IT", …)` non dà la stessa
 * stringa in Node e in Chrome, e una differenza fra i due è un errore
 * di idratazione su ogni riga della lista.
 */
export function fmtDataFirmata(ts: number): string {
  const d = new Date(ts);
  return `${d.getDate()} ${MESI_IT[d.getMonth()]} ${d.getFullYear()}`;
}

/** Giorno e mese in due cifre: «18/09». Il binario di una lista rada,
 *  dove l'ora non serve e la data sì. */
export function fmtGiornoMese(ts: number): string {
  const d = new Date(ts);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
}

/** Ogni quanto la lista cambia stacco. */
export type PassoRullo = "giorno" | "mese";

/**
 * Se una lista di dispacci va spezzata **per giorno** o **per mese**.
 *
 * Nasce da un difetto misurato. Gli stacchi di giornata sul rullo
 * funzionano perché il rullo è fitto: cento righe su sette giorni, cioè
 * un'etichetta ogni quattordici. Sulla scheda di un Paese no: i
 * cinquanta dispacci della Serbia stanno su **quarantatré giorni
 * diversi**, quindi lo stacco usciva quasi su ogni riga — quarantatré
 * linee orizzontali per cinquanta notizie, cioè esattamente il rumore
 * che gli stacchi dovevano togliere.
 *
 * La soglia è **due dispacci per giorno in media**. Sotto quella,
 * l'etichetta non raggruppa niente e si passa al mese; e allora il
 * binario a sinistra smette di mostrare l'ora — che in una lista rada
 * non dice nulla — e mostra il giorno.
 *
 * Sta qui e non accanto al componente perché la chiamano anche due
 * Server Component (le due rotte /regioni): una funzione esportata da
 * un modulo «use client» letta dal server è il riferimento al modulo,
 * non il suo valore.
 */
export function passoRullo(times: number[]): PassoRullo {
  if (times.length === 0) return "giorno";
  const giorni = new Set(
    times.map((t) => {
      const d = new Date(t);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    })
  ).size;
  return giorni * 2 <= times.length ? "giorno" : "mese";
}

/**
 * L'etichetta dello stacco **solo se questo dispaccio ne apre uno
 * nuovo** rispetto a quello che lo precede nella lista, altrimenti
 * `null`.
 *
 * Senza `precedente` (cioè in testa alla lista) l'etichetta esce
 * sempre: la prima riga deve dire di quando si sta leggendo.
 */
export function staccoRullo(
  passo: PassoRullo,
  ts: number,
  precedente?: number
): string | null {
  if (precedente != null) {
    const a = new Date(ts);
    const b = new Date(precedente);
    const stesso =
      passo === "giorno"
        ? isSameDay(ts, precedente)
        : a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
    if (stesso) return null;
  }
  return passo === "giorno" ? etichettaGiorno(ts) : etichettaMese(ts);
}

/** Che cosa scrive il binario a sinistra della riga: l'ora quando la
 *  lista è spezzata per giorno, la data quando è spezzata per mese. */
export function binarioRullo(passo: PassoRullo, ts: number): string {
  return passo === "giorno" ? fmtClockShort(ts) : fmtGiornoMese(ts);
}

/**
 * Data nella riga meta di un dispaccio.
 *
 * Di oggi → solo "HH:MM". Ripetere la data odierna su venti righe di
 * fila è rumore: in un rullo che si aggiorna in continuo, di un
 * dispaccio di oggi conta l'ora.
 *
 * Più vecchio → "dd/mm/yyyy HH:MM". Lì la data serve davvero, e l'ora
 * resta perché fra due dispacci dello stesso giorno l'ordine si legge
 * solo da quella.
 *
 * Prima faceva il contrario: data e ora per quelli di oggi, la sola
 * data per i vecchi.
 */
export function fmtArticleDate(ts: number): string {
  return isSameDay(ts, Date.now())
    ? fmtClockShort(ts)
    : `${fmtDate(ts)} ${fmtClockShort(ts)}`;
}

/**
 * La data di pubblicazione come si scrive nella testata di un
 * dispaccio: il giorno, e l'ora **solo se il dispaccio è di oggi**.
 *
 * Passata la mezzanotte di un pezzo conta il giorno: "11/09/2026 14:09"
 * su un dispaccio di tre settimane fa è una precisione che non serve a
 * nessuno e allunga una riga che ha già la targa del tipo, il paese, il
 * tema, la rubrica e il codice. Di uno uscito stamattina invece l'ora è
 * il dato che lo colloca, perché la data da sola direbbe "oggi" e
 * basta.
 *
 * Sta qui e non nel componente perché legge l'orologio: `Date.now()`
 * chiamato durante il render è impuro (`react-hooks/purity`), e chi
 * rende il valore deve comunque marcare l'elemento con
 * `suppressHydrationWarning`, perché una richiesta a cavallo della
 * mezzanotte può dare due risposte diverse fra server e browser.
 */
export function fmtDataPubblicazione(ts: number): string {
  return isSameDay(ts, Date.now())
    ? `${fmtDate(ts)} ${fmtClockShort(ts)}`
    : fmtDate(ts);
}

/** Sotto le due ore un dispaccio è "appena uscito" e quello che conta è
 *  quanto tempo è passato; oltre, conta l'ora in cui è uscito. */
const DUE_ORE_MS = 2 * 60 * 60 * 1000;

/**
 * Il "quando" di un dispaccio, in una sola forma.
 *
 * Fino a due ore → il relativo: "5 min fa", "1 h fa".
 * Da due ore in poi → l'ora: "13:43", e la data davanti se non è di oggi.
 *
 * Prima le due forme si vedevano insieme ("13:43 · 3 h fa"), che è due
 * volte la stessa informazione: appena uscito il relativo dice tutto e
 * l'orologio non serve, dopo qualche ora è il contrario, "7 h fa" non
 * dice a che ora è successo e obbliga a fare il conto a mente.
 */
export function fmtDispatchWhen(ts: number): string {
  const now = Date.now();
  return now - ts < DUE_ORE_MS && isSameDay(ts, now)
    ? relTime(ts)
    : fmtArticleDate(ts);
}

/** Upstream cases only carry the numeric Strapi row id (case_id/oi_id are
 *  null in the live DB), so we format it as a proper dispatch code: e.g.
 *  `OI-000001` or `OI-352085`. Zero-padded to six digits so short ids
 *  don't look accidental. */
export function fmtDispatchId(id: string | number): string {
  return `OI-${String(id).padStart(6, "0")}`;
}

export function truncate(str: string, n: number): string {
  if (str.length <= n) return str;
  return str.slice(0, n - 1).replace(/\s+\S*$/, "") + "…";
}

export type FlatEntity = { type: EntityKind; name: string };

export function flatEntities(ent: Entities): FlatEntity[] {
  const out: FlatEntity[] = [];
  for (const type of ENTITY_KINDS) {
    for (const name of ent[type] || []) out.push({ type, name });
  }
  return out;
}

/** Splits text into `{match, value}` segments for in-place highlight rendering. */
export type HighlightSegment = { match: boolean; value: string };

export function highlightMatch(text: string, q: string): HighlightSegment[] {
  if (!q) return [{ match: false, value: text }];
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const out: HighlightSegment[] = [];
  let i = 0;
  while (i < text.length) {
    const found = lower.indexOf(needle, i);
    if (found === -1) {
      out.push({ match: false, value: text.slice(i) });
      break;
    }
    if (found > i) out.push({ match: false, value: text.slice(i, found) });
    out.push({ match: true, value: text.slice(found, found + needle.length) });
    i = found + needle.length;
  }
  return out;
}

export function marketStatus(ts: number = Date.now()): string {
  const d = new Date(ts);
  const h = d.getHours();
  const day = d.getDay();
  if (day === 0 || day === 6) return "MERCATI CHIUSI";
  if (h >= 9 && h < 17) return "MERCATI APERTI";
  return "MERCATI CHIUSI";
}
